import { useState, useEffect } from 'react';
import { Play, Clock, Eye, Share2, ChevronRight, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getChannelVideos, getChannelInfo } from '../../services/youtubeBackendService';

function LatestVideos() {
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const channelInfo = getChannelInfo();

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🎬 Loading videos from India Juncation News...');
      const fetchedVideos = await getChannelVideos(20, !forceRefresh);

      if (fetchedVideos && fetchedVideos.length > 0) {
        setVideos(fetchedVideos);
        console.log(`✅ Loaded ${fetchedVideos.length} videos successfully!`);
      } else {
        setError('वीडियो लोड नहीं हो सके। कृपया बाद में पुनः प्रयास करें।');
      }
    } catch (err) {
      console.error('Error loading videos:', err);
      setError('वीडियो लोड करने में समस्या आई। कृपया बाद में प्रयास करें।');
    } finally {
      setLoading(false);
    }
  };

  const featuredVideo = videos[0] || null;
  const sidebarVideos = videos.slice(1, 6);
  const moreVideos = videos.slice(6, 12);

  const handleVideoClick = (video) => {
    // Navigate to VideoPlay page with video data
    navigate('/videoplay', { state: { video } });
  };

  // Video Card Component
  const VideoCard = ({ video, isSmall = false }) => (
    <div
      className={`bg-white rounded-lg overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer group ${isSmall ? '' : 'h-full'
        }`}
      onMouseEnter={() => setHoveredVideo(video.id)}
      onMouseLeave={() => setHoveredVideo(null)}
      onClick={() => handleVideoClick(video)}
    >
      <div className="relative overflow-hidden">
        <img
          src={video.thumbnail}
          alt={video.title}
          className={`w-full object-cover group-hover:scale-110 transition-transform duration-500 ${isSmall ? 'h-28 sm:h-32' : 'h-40 sm:h-48'
            }`}
          loading="lazy"
          onError={(e) => {
            e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
          }}
        />

        {/* Play overlay */}
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="bg-white/90 rounded-full p-3 sm:p-4 transform scale-90 group-hover:scale-100 transition-transform">
            <Play className="w-6 h-6 sm:w-8 sm:h-8 text-red-600 fill-red-600" />
          </div>
        </div>

        {/* Duration */}
        {video.duration && (
          <div className="absolute bottom-2 right-2 bg-black/90 text-white text-xs px-2 py-1 rounded font-semibold">
            {video.duration}
          </div>
        )}

        {/* Category badge */}
        {video.category && (
          <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded font-bold uppercase shadow-lg">
            {video.category}
          </div>
        )}

        {/* Play icon */}
        <div className="absolute top-2 right-2 bg-white rounded-full p-1 sm:p-1.5 shadow-lg">
          <Play className="w-3 h-3 sm:w-4 sm:h-4 text-red-600 fill-red-600" />
        </div>
      </div>

      <div className={`${isSmall ? 'p-2 sm:p-3' : 'p-3 sm:p-4'}`}>
        <h3 className={`font-bold group-hover:text-red-600 transition-colors ${isSmall ? 'text-xs sm:text-sm line-clamp-2' : 'text-sm sm:text-base line-clamp-2'}`}>
          {video.title}
        </h3>
        <div className={`flex items-center gap-2 sm:gap-3 text-gray-600 ${isSmall ? 'text-xs mt-1.5 sm:mt-2' : 'text-xs sm:text-sm mt-2 sm:mt-3'}`}>
          {video.views && (
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              <span className="hidden sm:inline">{video.views}</span>
              <span className="sm:hidden">{video.views.split(' ')[0]}</span>
            </span>
          )}
          {video.timeAgo && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {video.timeAgo}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-900 px-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 text-red-600 animate-spin mx-auto mb-4 sm:mb-6" />
          <h2 className="text-white text-xl sm:text-2xl font-bold mb-2">वीडियो लोड हो रहे हैं...</h2>
          <p className="text-gray-400 text-sm sm:text-base">India Juncation News से लेटेस्ट वीडियो fetch कर रहे हैं</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error || !featuredVideo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-black to-gray-900 p-4">
        <div className="text-center max-w-md">
          <div className="bg-red-600/20 border-2 border-red-600 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-red-400 text-xl sm:text-2xl font-bold mb-2 sm:mb-3">{error || 'वीडियो लोड नहीं हो सके'}</h2>
            <p className="text-gray-300 text-sm sm:text-base mb-4">
              CORS प्रॉक्सी के साथ समस्या हो सकती है। कृपया फिर से प्रयास करें।
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => loadVideos(true)}
              className="w-full flex items-center justify-center gap-2 bg-red-600 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-red-700 transition-all font-semibold shadow-lg hover:shadow-xl text-sm sm:text-base"
            >
              <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
              फिर से लोड करें
            </button>

            <a
              href={channelInfo.videosUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 bg-white/10 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-white/20 transition-all font-semibold text-sm sm:text-base"
            >
              <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5" />
              YouTube चैनल पर जाएं
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header Section */}
      <div className="py-3 sm:py-4 sticky top-0 z-40 shadow-lg bg-gradient-to-r from-gray-900 to-black backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4">
          <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-3">
            <h1 className="text-white text-lg sm:text-2xl md:text-3xl font-bold flex items-center gap-2 sm:gap-3">
              <div className="bg-gradient-to-br from-red-600 to-red-700 p-1.5 sm:p-2 rounded-lg shadow-lg">
                <Play className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
              </div>
              <span className="whitespace-nowrap">लेटेस्ट वीडियो</span>
              <span className="text-xs sm:text-sm font-normal text-gray-400 hidden md:inline">
                ({videos.length} वीडियो)
              </span>
            </h1>

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => loadVideos(true)}
                className="text-white flex items-center gap-1 sm:gap-2 hover:text-yellow-300 transition-colors text-xs sm:text-sm md:text-base"
                title="Refresh videos"
              >
                <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">रीफ्रेश</span>
              </button>

              <a
                href={channelInfo.videosUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white flex items-center gap-1 sm:gap-2 hover:text-yellow-300 transition-colors text-xs sm:text-sm md:text-base"
              >
                <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">चैनल</span>
              </a>

              <button
                onClick={() => navigate('/allvideos')}
                className="text-white flex items-center gap-1 hover:text-yellow-300 transition-colors font-semibold text-xs sm:text-sm md:text-base"
              >
                और देखें <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Sidebar - Video List (Hidden on mobile, shows on lg+) */}
          <div className="hidden lg:block space-y-4">
            {sidebarVideos.map((video) => (
              <div
                key={video.id}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group flex"
                onMouseEnter={() => setHoveredVideo(video.id)}
                onMouseLeave={() => setHoveredVideo(null)}
                onClick={() => handleVideoClick(video)}
              >
                <div className="relative w-32 xl:w-40 flex-shrink-0">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                    }}
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-white rounded-full p-2">
                      <Play className="w-5 h-5 xl:w-6 xl:h-6 text-red-600 fill-red-600" />
                    </div>
                  </div>
                  {video.duration && (
                    <div className="absolute bottom-2 right-2 bg-black/90 text-white text-xs px-1.5 py-0.5 rounded font-semibold">
                      {video.duration}
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-white rounded-full p-1 shadow">
                    <Play className="w-3 h-3 text-red-600 fill-red-600" />
                  </div>
                </div>
                <div className="p-3 flex-1">
                  <h3 className="font-bold text-xs xl:text-sm line-clamp-3 group-hover:text-red-600 transition-colors leading-snug">
                    {video.title}
                  </h3>
                  <div className="flex items-center gap-2 text-gray-600 text-xs mt-2">
                    {video.views && (
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {video.views.split(' ')[0]}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Center - Featured Video */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg overflow-hidden shadow-2xl">
              {/* Featured iframe */}
              <div className="relative bg-black" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={`${featuredVideo.embedUrl}?controls=1&modestbranding=1&rel=0`}
                  title={featuredVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>

              <div className="p-4 sm:p-6 bg-gradient-to-r from-gray-50 to-white">
                <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3 text-gray-900">
                  {featuredVideo.title}
                </h2>

                <div className="flex items-center justify-between flex-wrap gap-3 sm:gap-4">
                  <div className="flex items-center gap-2 sm:gap-4 text-gray-600 flex-wrap">
                    {featuredVideo.category && (
                      <span className="bg-yellow-400 text-black px-2 sm:px-4 py-1 sm:py-1.5 rounded-lg font-bold uppercase text-xs sm:text-sm shadow">
                        {featuredVideo.category}
                      </span>
                    )}
                    {featuredVideo.views && (
                      <span className="flex items-center gap-1 sm:gap-2 font-semibold text-xs sm:text-base">
                        <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="hidden sm:inline">{featuredVideo.views}</span>
                        <span className="sm:hidden">{featuredVideo.views.split(' ')[0]}</span>
                      </span>
                    )}
                    {featuredVideo.timeAgo && (
                      <span className="flex items-center gap-1 sm:gap-2 text-xs sm:text-base">
                        <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                        {featuredVideo.timeAgo}
                      </span>
                    )}
                  </div>

                  <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                    <a
                      href={featuredVideo.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 sm:gap-2 bg-red-600 text-white px-3 sm:px-6 py-2 sm:py-2.5 rounded-lg hover:bg-red-700 transition-all font-semibold shadow-md hover:shadow-lg hover:scale-105 text-xs sm:text-base"
                    >
                      <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">YouTube पर देखें</span>
                      <span className="sm:hidden">YouTube</span>
                    </a>
                    <button
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({ title: featuredVideo.title, url: featuredVideo.videoUrl });
                        } else {
                          navigator.clipboard.writeText(featuredVideo.videoUrl);
                          alert('लिंक कॉपी हो गया!');
                        }
                      }}
                      className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 sm:gap-2 bg-gray-200 text-gray-800 px-3 sm:px-6 py-2 sm:py-2.5 rounded-lg hover:bg-gray-300 transition-all font-semibold shadow-md text-xs sm:text-base"
                    >
                      <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      शेयर
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Sidebar Videos - Shows below featured video on mobile */}
            <div className="lg:hidden mt-4 sm:mt-6">
              <h2 className="text-white text-base sm:text-xl font-bold mb-3 sm:mb-4 flex items-center gap-2">
                <span className="w-1 h-6 sm:h-8 bg-yellow-400 rounded"></span>
                अन्य वीडियो
              </h2>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {sidebarVideos.slice(0, 4).map((video) => (
                  <VideoCard key={video.id} video={video} isSmall={true} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Buttons */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-6 sm:py-8 text-center space-y-3 sm:space-y-4">
        <button
          onClick={() => navigate('/allvideos')}
          className="bg-white text-black px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-bold hover:bg-yellow-400 transition-all shadow-lg hover:shadow-xl hover:scale-105 text-sm sm:text-base"
        >
          और वीडियो लोड करें
        </button>
      </div>
    </div>
  );
}

export default LatestVideos;